import { createApi } from '@reduxjs/toolkit/query/react';
import { AvailableDataRangeCategory } from 'src/utils/quarterUtils';
import { baseQueryWithTokenHandling } from '../baseQuery';
import {
  BULK_EXPORTS_TYPE,
  TERRITORY_EXPORT_TYPE,
  REGION_ANALYTICS_TYPE,
  TRENDING_ANALYTICS_TYPE,
  AVAILABLE_DATA_RANGE_TYPE,
} from './constants';
import { buildSearchParamsAuto } from '../utils';
import type {
  TerritoryTrendsInsight,
  TerritoryBreakdownInsight,
  TerritoryCodeSetBreakdownInsight,
  TerritoryAspInsight,
  TerritoryReimbursementInsight,
  TerritoryInsightsResponse,
  CreateBulkExportParams,
  GetTerritoryExportParams,
} from './types';

interface PaginatedRegionsResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: MedScout.Region[];
}

/**
 * Backend-derived available TA date ranges. `default` feeds every insight
 * picker; `reimbursement` feeds the narrower Average Reimbursement picker.
 */
export interface AvailableDataRangeResponse {
  default: AvailableDataRangeCategory;
  reimbursement: AvailableDataRangeCategory;
}

interface AnalyticsQueryParams {
  sort?: string;
  order?: 'asc' | 'desc';
}

interface AnalyticsQueryArgs {
  data: Record<string, any>;
  params?: AnalyticsQueryParams;
}

interface RegionAnalyticsQueryArgs {
  queryParams?: Record<string, any>;
}

export const analyticsApi = createApi({
  reducerPath: 'analytics',
  baseQuery: baseQueryWithTokenHandling,
  tagTypes: [
    BULK_EXPORTS_TYPE,
    TERRITORY_EXPORT_TYPE,
    REGION_ANALYTICS_TYPE,
    TRENDING_ANALYTICS_TYPE,
    AVAILABLE_DATA_RANGE_TYPE,
  ],
  endpoints: (builder) => ({
    /**
     * Get all regions (paginated)
     * Recursively fetches all pages and returns combined results
     */
    getRegionAnalytics: builder.query<MedScout.Region[], RegionAnalyticsQueryArgs | void>({
      async queryFn(arg, _queryApi, _extraOptions, fetchWithBQ) {
        const allRegions: MedScout.Region[] = [];
        const queryParams =
          arg && typeof arg === 'object' ? arg.queryParams : undefined;
        const searchParams = queryParams
          ? buildSearchParamsAuto(queryParams)
          : '';
        let url = `v1/accounts/regions/?page=1&pageSize=1000${
          searchParams ? `&${searchParams}` : ''
        }`;

        while (url) {
          const result = await fetchWithBQ(url);
          if (result.error) return { error: result.error };

          const data = result.data as PaginatedRegionsResponse;
          allRegions.push(...data.results);

          // Get next page URL if it exists
          if (data.next) {
            url = data.next.split('api/')[1];
          } else {
            url = '';
          }
        }

        return { data: allRegions };
      },
      providesTags: [{ type: REGION_ANALYTICS_TYPE, id: 'ALL' }],
    }),
    createBulkExportV2: builder.mutation<void, CreateBulkExportParams>({
      query: ({ data, type }) => {
        const queryParams = buildSearchParamsAuto({ type });
        return {
          url: `v1/analytics/territory-insights/bulk-export/?${queryParams}`,
          method: 'POST',
          body: data,
        };
      },
      invalidatesTags: (_, __, { territoryId }) => [
        { type: BULK_EXPORTS_TYPE, id: territoryId },
      ],
    }),
    getTerritoryExport: builder.query<Blob, GetTerritoryExportParams>({
      query: ({ territoryId }) => {
        return {
          url: `v1/accounts/territories/${territoryId}/export/`,
          method: 'GET',
          responseHandler: (response) => response.blob(),
        };
      },
      providesTags: (result, error, { territoryId }) => [
        { type: TERRITORY_EXPORT_TYPE, id: territoryId },
      ],
    }),
    // Table data
    getTrendingAnalytics: builder.query<TerritoryInsightsResponse<TerritoryTrendsInsight>, AnalyticsQueryArgs>({
      query: ({ data, params }: AnalyticsQueryArgs) => {
        const queryParams = buildSearchParamsAuto({ ...params });
        return {
          url: `v1/analytics/territory-insights/trends/?${queryParams}`,
          method: 'POST',
          body: data,
        };
      },
      providesTags: (result) =>
        result
          ? [
              ...result.results.map(({ id }: { id: number }) => ({
                type: TRENDING_ANALYTICS_TYPE as typeof TRENDING_ANALYTICS_TYPE,
                id,
              })),
              { type: TRENDING_ANALYTICS_TYPE, id: 'TRENDS' },
            ]
          : [{ type: TRENDING_ANALYTICS_TYPE, id: 'TRENDS' }],
    }),
    getCodeSetAnalytics: builder.query<TerritoryInsightsResponse<TerritoryBreakdownInsight>, AnalyticsQueryArgs>({
      query: ({ data, params }: AnalyticsQueryArgs) => {
        const queryParams = buildSearchParamsAuto({ ...params });
        return {
          url: `v1/analytics/territory-insights/code-set/?${queryParams}`,
          method: 'POST',
          body: data,
        };
      },
      providesTags: (result) =>
        result
          ? [
              ...result.results.map(({ id }: { id: number }) => ({
                type: TRENDING_ANALYTICS_TYPE as typeof TRENDING_ANALYTICS_TYPE,
                id,
              })),
              { type: TRENDING_ANALYTICS_TYPE, id: 'CODE_SETS' },
            ]
          : [{ type: TRENDING_ANALYTICS_TYPE, id: 'CODE_SETS' }],
    }),
    /** Backs the "Volume by Code" insight — one column per code in a single code set. */
    getCodeSetBreakdownAnalytics: builder.query<TerritoryInsightsResponse<TerritoryCodeSetBreakdownInsight>, AnalyticsQueryArgs>({
      query: ({ data, params }: AnalyticsQueryArgs) => {
        const queryParams = buildSearchParamsAuto({ ...params });
        return {
          url: `v1/analytics/territory-insights/code-set-breakdown/?${queryParams}`,
          method: 'POST',
          body: data,
        };
      },
      providesTags: (result) =>
        result
          ? [
              ...result.results.map(({ id }: { id: number }) => ({
                type: TRENDING_ANALYTICS_TYPE as typeof TRENDING_ANALYTICS_TYPE,
                id,
              })),
              { type: TRENDING_ANALYTICS_TYPE, id: 'CODE_SET_BREAKDOWN' },
            ]
          : [{ type: TRENDING_ANALYTICS_TYPE, id: 'CODE_SET_BREAKDOWN' }],
    }),
    getPayerMixAnalytics: builder.query<TerritoryInsightsResponse<TerritoryBreakdownInsight>, AnalyticsQueryArgs>({
      query: ({ data, params }: AnalyticsQueryArgs) => {
        const queryParams = buildSearchParamsAuto({ ...params });
        return {
          url: `v1/analytics/territory-insights/payer-mix/?${queryParams}`,
          method: 'POST',
          body: data,
        };
      },
      providesTags: (result) =>
        result
          ? [
              ...result.results.map(({ id }: { id: number }) => ({
                type: TRENDING_ANALYTICS_TYPE as typeof TRENDING_ANALYTICS_TYPE,
                id,
              })),
              { type: TRENDING_ANALYTICS_TYPE, id: 'PAYER_MIX' },
            ]
          : [{ type: TRENDING_ANALYTICS_TYPE, id: 'PAYER_MIX' }],
    }),
    getAspAnalytics: builder.query<TerritoryInsightsResponse<TerritoryAspInsight>, AnalyticsQueryArgs>({
      query: ({ data, params }: AnalyticsQueryArgs) => {
        const queryParams = buildSearchParamsAuto({ ...params });
        return {
          url: `v1/analytics/territory-insights/average-sale-price/?${queryParams}`,
          method: 'POST',
          body: data,
        };
      },
      providesTags: (result) =>
        result
          ? [
              ...result.results.map(({ id }: { id: number }) => ({
                type: TRENDING_ANALYTICS_TYPE as typeof TRENDING_ANALYTICS_TYPE,
                id,
              })),
              { type: TRENDING_ANALYTICS_TYPE, id: 'ASP' },
            ]
          : [{ type: TRENDING_ANALYTICS_TYPE, id: 'ASP' }],
    }),
    getPlaceOfServiceAnalytics: builder.query<TerritoryInsightsResponse<TerritoryBreakdownInsight>, AnalyticsQueryArgs>({
      query: ({ data, params }: AnalyticsQueryArgs) => {
        const queryParams = buildSearchParamsAuto({ ...params });
        return {
          url: `v1/analytics/territory-insights/place-of-service/?${queryParams}`,
          method: 'POST',
          body: data,
        };
      },
      providesTags: (result) =>
        result
          ? [
              ...result.results.map(({ id }: { id: number }) => ({
                type: TRENDING_ANALYTICS_TYPE as typeof TRENDING_ANALYTICS_TYPE,
                id,
              })),
              { type: TRENDING_ANALYTICS_TYPE, id: 'PLACE_OF_SERVICE' },
            ]
          : [{ type: TRENDING_ANALYTICS_TYPE, id: 'PLACE_OF_SERVICE' }],
    }),
    getSiteOfCareAnalytics: builder.query<TerritoryInsightsResponse<TerritoryBreakdownInsight>, AnalyticsQueryArgs>({
      query: ({ data, params }: AnalyticsQueryArgs) => {
        const queryParams = buildSearchParamsAuto({ ...params });
        return {
          url: `v1/analytics/territory-insights/site-of-care/?${queryParams}`,
          method: 'POST',
          body: data,
        };
      },
      providesTags: (result) =>
        result
          ? [
              ...result.results.map(({ id }: { id: number }) => ({
                type: TRENDING_ANALYTICS_TYPE as typeof TRENDING_ANALYTICS_TYPE,
                id,
              })),
              { type: TRENDING_ANALYTICS_TYPE, id: 'SITE_OF_CARE' },
            ]
          : [{ type: TRENDING_ANALYTICS_TYPE, id: 'SITE_OF_CARE' }],
    }),
    getReimbursementAnalytics: builder.query<TerritoryInsightsResponse<TerritoryReimbursementInsight>, AnalyticsQueryArgs>({
      query: ({ data, params }: AnalyticsQueryArgs) => {
        const queryParams = buildSearchParamsAuto({ ...params });
        return {
          url: `v1/analytics/territory-insights/reimbursement/?${queryParams}`,
          method: 'POST',
          body: data,
        };
      },
      providesTags: (result) =>
        result
          ? [
              ...result.results.map(({ id }: { id: number }) => ({
                type: TRENDING_ANALYTICS_TYPE as typeof TRENDING_ANALYTICS_TYPE,
                id,
              })),
              { type: TRENDING_ANALYTICS_TYPE, id: 'REIMBURSEMENT' },
            ]
          : [{ type: TRENDING_ANALYTICS_TYPE, id: 'REIMBURSEMENT' }],
    }),
    getAvailableDataRange: builder.query<AvailableDataRangeResponse, void>({
      query: () => ({
        url: 'v1/analytics/territory-insights/available-data-range/',
        method: 'GET',
      }),
      providesTags: [
        { type: AVAILABLE_DATA_RANGE_TYPE, id: 'AVAILABLE_DATA_RANGE' },
      ],
    }),
  }),
});

export const {
  useCreateBulkExportV2Mutation,
  useGetRegionAnalyticsQuery,
  useGetTerritoryExportQuery,
  useLazyGetTerritoryExportQuery,
  useGetTrendingAnalyticsQuery,
  useGetCodeSetAnalyticsQuery,
  useGetCodeSetBreakdownAnalyticsQuery,
  useGetPayerMixAnalyticsQuery,
  useGetAspAnalyticsQuery,
  useGetPlaceOfServiceAnalyticsQuery,
  useGetSiteOfCareAnalyticsQuery,
  useGetReimbursementAnalyticsQuery,
  useGetAvailableDataRangeQuery,
} = analyticsApi;
